import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { MetaBox } from '@/components/admin/MetaBox';
import { ErrorState, ForbiddenState, SkeletonRows } from '@/components/admin/states';
import { MemberEventsTab } from '@/features/members/MemberActivityTabs';
import { fetchMember } from '@/features/members/api';
import { MemberRelatedNav } from '@/features/members/MemberRelatedNav';
import { ApiError } from '@/lib/api/http';

/**
 * 参加イベント一覧（D-149）。
 * D-144 では会員詳細のイベントタブを `?tab=events` で開いていたが、
 * 「参加イベント一覧なので、イベントの一覧出すだけでよい。プロフィールも不要」
 * というユーザー指摘を受け、一覧だけの独立ページにした。
 * 行を押すと、**その受講生のそのイベントでの結果**（出欠・課題の回答など）を
 * まとめた画面へ移動する（D-150）。
 */
export function MemberEventsPage(): ReactElement {
  const { memberId = '' } = useParams();
  const query = useQuery({
    queryKey: ['member', memberId],
    queryFn: () => fetchMember(memberId),
    enabled: Boolean(memberId),
  });

  if (query.isPending) return <SkeletonRows rows={6} />;
  if (query.isError) {
    const forbidden = query.error instanceof ApiError && query.error.status === 403;
    return forbidden ? (
      <ForbiddenState />
    ) : (
      <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
    );
  }

  const member = query.data;

  return (
    <>
      <PageHeader
        title={`${member.name} さんの参加イベント一覧`}
        description="時期と種別で絞り込めます。行を押すと、その回の結果（課題やアンケートの回答など）がまとめて開きます。"
      />
      <MemberRelatedNav memberId={member.id} current="events" />
      <MetaBox title="参加イベント">
        <MemberEventsTab memberId={member.id} />
      </MetaBox>
      <Link to={`/members/${member.id}`} className="mt-4 inline-block text-wpaccent hover:underline">
        ← 会員詳細に戻る
      </Link>
    </>
  );
}
